import React, { useMemo, useState } from "react";
import { Slider } from "@material-ui/core";
import { PriceFreezeOfferData } from "@b2bportal/price-freeze-api";
import "./styles.scss";
import {
  HOURS_IN_A_DAY,
  secondsToDays,
  secondsToHours,
} from "@hopper-b2b/utilities";
import { useI18nContext } from "@hopper-b2b/i18n";

export interface IPriceFreezeDurationSlider {
  handleDurationChange: (val: number) => void;
  priceFreezeOfferData: PriceFreezeOfferData[];
  selectedPriceFreezeOfferIndex: number;
}

export const PriceFreezeDurationSlider = (
  props: IPriceFreezeDurationSlider
) => {
  const {
    handleDurationChange,
    priceFreezeOfferData,
    selectedPriceFreezeOfferIndex,
  } = props;

  const normalizeToPriceFreezeOfferDataIndex = (val: number) => val - 1;
  const normalizeToSliderIndex = (val: number) => val + 1;

  const [sliderValueIndex, setSliderValueIndex] = useState(
    normalizeToSliderIndex(selectedPriceFreezeOfferIndex)
  );
  const { t } = useI18nContext();

  const marks = useMemo(() => {
    const marks0 = priceFreezeOfferData.map((data, index) => {
      return {
        value: index + 1,
        label:
          secondsToHours(data.secondsTtl) < HOURS_IN_A_DAY
            ? `${secondsToHours(data.secondsTtl)}${t(
                "priceFreezePurchase.offerCard.hourCode"
              )}`
            : `${secondsToDays(data.secondsTtl)}${t(
                "priceFreezePurchase.offerCard.dayCode"
              )}`,
      };
    });

    marks0.unshift({ value: 0, label: "" });
    marks0.push({ value: marks0.length, label: "" });
    return marks0;
  }, [priceFreezeOfferData]);

  const disableFirstAndLastValueOnSlider = (index: number) => {
    const FIRST_INDEX = 0;
    const LAST_INDEX = marks.length - 1;

    return index === FIRST_INDEX
      ? 1
      : index === LAST_INDEX
      ? LAST_INDEX - 1
      : index;
  };

  const onDurationChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number | number[]
  ) => {
    //Disable first and lastvalue
    const value = disableFirstAndLastValueOnSlider(newValue as number);
    setSliderValueIndex(value);

    //normalize to correct index
    handleDurationChange(normalizeToPriceFreezeOfferDataIndex(value));
  };

  const valuetext = (value: number) => {
    const priceFreezeSecondsTtl =
      priceFreezeOfferData[normalizeToPriceFreezeOfferDataIndex(value)]
        ?.secondsTtl ?? 0;
    return secondsToHours(priceFreezeSecondsTtl) < HOURS_IN_A_DAY
      ? `${secondsToHours(priceFreezeSecondsTtl)} ${t(
          "priceFreezePurchase.offerCard.hours"
        )}`
      : `${secondsToDays(priceFreezeSecondsTtl)} ${
          secondsToDays(priceFreezeSecondsTtl) === 1
            ? t("priceFreezePurchase.offerCard.day")
            : t("priceFreezePurchase.offerCard.days")
        }`;
  };

  return (
    <div className="price-freeze-duration-block">
      <div className="price-freeze-duration-picker">
        <Slider
          key={`price-freeze-duration-slider-${selectedPriceFreezeOfferIndex}`}
          aria-label="Price Freeze Duration"
          defaultValue={selectedPriceFreezeOfferIndex}
          getAriaValueText={valuetext}
          marks={marks}
          onChangeCommitted={onDurationChange}
          value={sliderValueIndex}
          min={0}
          max={marks?.length ? marks?.length - 1 : 1}
        />
      </div>
    </div>
  );
};
